import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useServiceContext } from '../../contexts/ServiceProvider';
import useServiceActions from '../../hooks/APIHooks/appointments/useServiceActions';
import { Service } from '../../types/services';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import ConsentModal from '../CommonComps/Modals/ConsentModal';
import CreateEditServiceModal from '../CommonComps/Modals/CreateEditServiceModal';

const ServiceMutationModals = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  // integration of context hooks here
  const {
    service,
    showUpdateModal,
    setShowUpdateModal,
    showDeleteModal,
    setShowDeleteModal,
    showAddToPopularModal,
    setShowAddToPopularModal,
  } = useServiceContext();

  // integration of API hooks here
  const { deleteServiceByIdHandler, updateServiceHandler } =
    useServiceActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete service feature declared here
  const deleteServiceHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteServiceByIdHandler
        .mutateAsync({ serviceId: (service as Service).id })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Service Deleted!!');

            setShowDeleteModal(false);
            queryClient.invalidateQueries({ queryKey: ['service-list'] });
          } else {
            toast.error(res.data.message || 'Service Deleting Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  const addRemoveServiceToPopularHandler = async () => {
    try {
      if (service) {
        setIsAdding(true);

        const body = new FormData();

        body.append(
          'is_most_popular',
          !service.is_most_popular ? 'true' : 'false'
        );

        await updateServiceHandler
          .mutateAsync({ serviceId: service.id as number, body })
          .then((res) => {
            if (res.is_success) {
              toast.success(
                !service.is_most_popular
                  ? 'Added To Popular!!'
                  : 'Removed From Popular!!'
              );

              setShowAddToPopularModal(false);

              queryClient.invalidateQueries({ queryKey: ['service-list'] });
            } else {
              toast.error(res.message || 'Updating Popular Service Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsAdding(false);
    }
  };

  // rendering service mutation modals component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateModal && (
          <CreateEditServiceModal
            title='update'
            service={service as Service}
            handleModalClose={() => setShowUpdateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this service? Once you delete
                it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteServiceHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showAddToPopularModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to{' '}
                {!service?.is_most_popular ? 'add' : 'remove'} this service{' '}
                <span className='font-semibold text-base-orange'>
                  ({service?.name})
                </span>{' '}
                to popular list?
              </>
            }
            isLoading={isAdding}
            yesBtnTxt='Yes'
            yesBtnVariant={!service?.is_most_popular ? 'success' : 'danger'}
            noBtnTxt='No'
            noBtnVariant={!service?.is_most_popular ? 'danger' : 'success'}
            mutationHandler={addRemoveServiceToPopularHandler}
            handleModalClose={() => setShowAddToPopularModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default ServiceMutationModals;
